import * as React from "react";
import { useQuery } from "react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { LoadingDots } from "web-ui";

import { getMajorMinorVersions } from "api/dashboardBackend";
import DuoErrorScreen, { DuoErrorType } from "components/DuoErrorScreen";
import ChangelogContent from "components/changelog/ChangelogContent";
import styles from "components/changelog/ChangelogPage.module.scss";
import VersionSelect from "components/common/VersionSelect";
import useErrorScreen from "components/useErrorScreen";
import { ALL_PLATFORMS, type PlatformId } from "util/platform";

const ChangelogPage = () => {
  const { platform } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [fromVersion, setFromVersion] = React.useState(
    searchParams.get("fromVersion") ?? undefined,
  );
  const [toVersion, setToVersion] = React.useState(
    searchParams.get("toVersion") ?? undefined,
  );
  const { isError, error, isLoading, data } = useQuery({
    queryFn: () => getMajorMinorVersions(platform as PlatformId),
    queryKey: [platform],
  });

  const errorComponent = useErrorScreen(isError, error);

  if (errorComponent) {
    return errorComponent;
  }

  // eslint-disable-next-line
  const includedPlatforms = (platform: string): platform is PlatformId =>
    ALL_PLATFORMS.includes(platform);

  if (platform === undefined || !includedPlatforms(platform)) {
    return (
      <DuoErrorScreen
        error={Error(`Invalid platform: ${platform}.`)}
        errorType={DuoErrorType.UnknownError}
      />
    );
  }

  // It's intentional to set both fromVersion and toVersion to the same
  // value so we don't load an enormous amount of changelog when people goes
  // back to an ancient release version.
  const fromVersionChangeCallback = (value: string) => {
    setFromVersion(value);
    setToVersion(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("fromVersion", value);
    newParams.set("toVersion", value);
    setSearchParams(newParams);
  };

  const toVersionChangeCallback = (value: string) => {
    setToVersion(value);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("toVersion", value);
    setSearchParams(newParams);
  };

  if (fromVersion === undefined || isLoading) {
    if (data !== undefined && data.length > 0) {
      const version = `${data[0].major}.${data[0].minor}`;
      fromVersionChangeCallback(version);
    }
    return <LoadingDots type="screen-white" />;
  }

  return (
    <>
      <div className={styles.queryContainer}>
        <VersionSelect
          onChangeCallback={fromVersionChangeCallback}
          platformId={platform}
          queryPrompt="From Version:"
          value={fromVersion ?? "Loading..."}
        />
        <VersionSelect
          onChangeCallback={toVersionChangeCallback}
          platformId={platform}
          queryPrompt="To Version:"
          value={toVersion ?? fromVersion ?? "Loading..."}
        />
        <Link className={styles.closeButton} title="Close changelog" to="/">
          ✕
        </Link>
      </div>

      <div>
        <ChangelogContent
          fromVersion={fromVersion}
          platformId={platform}
          toVersion={toVersion ?? fromVersion}
        />
      </div>
    </>
  );
};

export default ChangelogPage;
